import { gsap, ScrollTrigger } from '@theme/gsap';

const textRevealInstances = new Map();

function splitTextNode(node) {
  if (!(node instanceof Text)) return;
  if (!node.textContent || !node.textContent.trim()) return;
  if (!(node.parentNode instanceof HTMLElement)) return;

  const fragment = document.createDocumentFragment();
  const parts = node.textContent.split(/(\s+)/);

  parts.forEach((part) => {
    if (!part) return;

    if (/^\s+$/.test(part)) {
      fragment.appendChild(document.createTextNode(part));
      return;
    }

    const word = document.createElement('span');
    word.dataset.textScrollRevealWord = '';
    word.textContent = part;
    fragment.appendChild(word);
  });

  node.parentNode.replaceChild(fragment, node);
}

function wrapWords(element) {
  if (!(element instanceof HTMLElement) || element.dataset.textScrollRevealPrepared === 'true') return;

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!(node instanceof Text)) return NodeFilter.FILTER_REJECT;
      if (!node.textContent || !node.textContent.trim()) return NodeFilter.FILTER_REJECT;
      if (!(node.parentElement instanceof HTMLElement)) return NodeFilter.FILTER_REJECT;
      if (node.parentElement.closest('[data-text-scroll-reveal-word]')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const textNodes = [];
  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode);
    currentNode = walker.nextNode();
  }

  textNodes.forEach(splitTextNode);
  element.dataset.textScrollRevealPrepared = 'true';
}

function cleanupTextReveal(element) {
  const instance = textRevealInstances.get(element);
  if (!instance) return;

  instance.timeline.scrollTrigger?.kill();
  instance.timeline.kill();
  textRevealInstances.delete(element);
}

function initTextReveal(element) {
  if (!(element instanceof HTMLElement)) return;

  wrapWords(element);
  cleanupTextReveal(element);

  const words = element.querySelectorAll('[data-text-scroll-reveal-word]');
  if (!words.length) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    gsap.set(words, { opacity: 1 });
    return;
  }

  gsap.set(words, { opacity: 0.2 });

  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger: element,
      start: 'top 82%',
      end: 'bottom 38%',
      scrub: true,
    },
  });

  timeline.to(words, {
    duration: 1,
    ease: 'none',
    opacity: 1,
    stagger: {
      each: 0.08,
      from: 'start',
    },
  });

  textRevealInstances.set(element, { timeline });
}

function initTextReveals(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;

  scope.querySelectorAll('[data-text-scroll-reveal]').forEach((element) => {
    initTextReveal(element);
  });
}

function cleanupTextReveals(root = document) {
  const scope = root instanceof HTMLElement || root instanceof Document ? root : document;

  scope.querySelectorAll('[data-text-scroll-reveal]').forEach((element) => {
    cleanupTextReveal(element);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initTextReveals(), { once: true });
} else {
  initTextReveals();
}

document.addEventListener('shopify:section:load', (event) => {
  initTextReveals(event.target);
  ScrollTrigger.refresh();
});

document.addEventListener('shopify:section:reorder', () => {
  ScrollTrigger.refresh();
});

document.addEventListener('shopify:section:unload', (event) => {
  cleanupTextReveals(event.target);
  ScrollTrigger.refresh();
});
